import {useCallback} from "react"

import {ApolloClient, ApolloLink, DocumentNode, from, HttpLink, InMemoryCache} from "@apollo/client"
import {onError} from "@apollo/client/link/error"
import {RetryLink} from "@apollo/client/link/retry"
import {OptionsObject, useSnackbar} from "notistack"
import {v4 as uuidv4} from "uuid"

import {createAuthLink, createUnauthorizedRetryLink} from "@auth/authLinks"
import {isUnauthorized} from "@auth/utils/token"

// Personalized type for the ApiFetchParam
type FetchParams = {
    url: string | undefined
    query: DocumentNode
    variables: object | undefined
    odmToken?: string
    type: "mutation" | "query"
    isApiKey?: boolean
    cache?: InMemoryCache
    customToken?: string
    restore?: boolean
    suppressErrorToast?: boolean
}

// Personalized type for the toaster options
interface CustomOptionsObject extends OptionsObject {
    type?: string
    title?: string
    code?: number
    id?: string
    createdAt: string
}

/**
 * Returns the current date and time formatted as (dd MMM yyyy hh:mm).
 *
 * @returns {string} The formatted stamp.
 */
function createdAt(): string {
    const now = new Date()

    const formattedDate = new Intl.DateTimeFormat("fr-FR", {
        year: "numeric",
        month: "short",
        day: "2-digit"
    }).format(now)

    const formattedTime = new Intl.DateTimeFormat("fr-FR", {
        hour: "2-digit",
        minute: "2-digit"
    }).format(now)

    return `${formattedDate} ${formattedTime}`
}

type DeepOmitTypename<T> = T extends null | undefined
    ? T
    : T extends (infer U)[]
      ? DeepOmitTypename<U>[]
      : T extends object
        ? {[K in keyof T as K extends "__typename" ? never : K]: DeepOmitTypename<T[K]>}
        : T

/**
 * Recursively removes `__typename`, so a value read from the cache can be sent
 * back as a mutation input.
 *
 * @template T
 * @param {T} value - The value to clean.
 * @returns {DeepOmitTypename<T>} The value, without any `__typename`.
 */
const removeTypename = <T>(value: T): DeepOmitTypename<T> => {
    if (value === null || value === undefined) {
        return value as DeepOmitTypename<T>
    }

    if (Array.isArray(value)) {
        return value.map(removeTypename) as DeepOmitTypename<T>
    }

    if (typeof value === "object" && Object.keys(value)?.length > 0) {
        const newObj: any = {}

        for (const key in value) {
            if (key !== "__typename" && Object.prototype.hasOwnProperty.call(value, key)) {
                newObj[key] = removeTypename(value[key])
            }
        }

        return newObj as DeepOmitTypename<T>
    }

    return value as DeepOmitTypename<T>
}

/** Strips `__typename` from the operation variables. */
const cleanTypenameLink = new ApolloLink((operation, forward) => {
    if (operation.variables) {
        operation.variables = removeTypename(operation.variables)
    }

    return forward(operation)
})

export function useApiFetch() {
    const {enqueueSnackbar} = useSnackbar()

    // Fonction fetchData pour exécuter une requête API
    const sendApiRequest = useCallback(
        async (params: FetchParams) => {
            const {url, query, variables, odmToken, type, isApiKey, customToken, suppressErrorToast} = params

            const retryLink = new RetryLink({
                delay: {initial: 2000, max: 7, jitter: false},
                attempts: {max: 1}
            })

            const errorLink = onError(({graphQLErrors, networkError}) => {
                if (suppressErrorToast) {
                    return
                }

                if (graphQLErrors?.length) {
                    graphQLErrors.forEach(({message}) => {
                        enqueueSnackbar({
                            type: "error",
                            title: "Error",
                            message: message,
                            id: uuidv4(),
                            createdAt: createdAt()
                        } as CustomOptionsObject)
                    })

                    return
                }

                if (networkError) {
                    const sessionExpired = isUnauthorized(networkError)

                    console.error(`Erreur serveur: ${networkError}`)
                    enqueueSnackbar({
                        type: "error",
                        title: "Error",
                        message: sessionExpired ? "Session expirée" : "Erreur Serveur",
                        code: sessionExpired ? 401 : 500,
                        id: uuidv4(),
                        createdAt: createdAt()
                    } as CustomOptionsObject)
                }
            })

            const client = new ApolloClient({
                cache: new InMemoryCache({addTypename: false}),
                devtools: {enabled: import.meta.env.DEV},
                link: from([
                    cleanTypenameLink,
                    retryLink,
                    errorLink,
                    createUnauthorizedRetryLink(),
                    createAuthLink({isApiKey, odmToken, customToken}),
                    new HttpLink({uri: url})
                ])
            })

            if (type === "mutation") {
                return await client.mutate({mutation: query, variables, fetchPolicy: "no-cache"})
            }

            return await client.query({query, variables, fetchPolicy: "no-cache"})
        },
        [enqueueSnackbar]
    )

    return {sendApiRequest}
}
